import { Field, FieldProps } from 'formik';
import React, { useCallback, useMemo } from 'react';
import {
  FormPageInputData,
  FormPageInputItemText,
  TextProperty,
  TEXT_PROPERTY_INPUT_TYPE,
} from 'shared';
import { useSearchForAddressByZipCode } from '../../../hooks';
import {
  useAssortedDrillDownProperties,
  useCurrentFormPage,
} from '../../../store';
import { FormFieldInputProps, FormFieldProps } from '../shared';
import { useFieldPropsHandler } from '../useFieldPropsHandler';
import { TextInputExcludeZipCode } from './TextInputExcludeZipCode';
import { TextZipCodeInput } from './TextZipCodeInput';
import { autoCompleteAddress } from './autoCompleteAddress';
import { convertInputTextValue, isZipCodeInputFormat } from './shared';

type Props = FormFieldProps<TextProperty>;
type InputProps = FormFieldInputProps<TextProperty, FormPageInputItemText>;

const Container: React.FC<
  Props & {
    fieldProps: FieldProps<FormPageInputItemText, FormPageInputData>;
  }
> = ({ fieldProps, property, disabled, submitted, design, reEnter }) => {
  const { searching, searchForAddress } = useSearchForAddressByZipCode();
  const formPage = useCurrentFormPage();
  const { propertyId, stringConvertRule, inputFormat } = property;
  const {
    fieldTouched,
    fieldErrorMessages,
    updateFieldValue,
    updateFieldTouched,
  } = useFieldPropsHandler<FormPageInputItemText>({
    fieldProps,
    propertyId,
  });

  const handleChange: InputProps['onChange'] = useCallback(
    (key, updateValue) => {
      updateFieldValue(key, updateValue);
    },
    [updateFieldValue]
  );
  const handleBlur: InputProps['onBlur'] = useCallback(
    (key, updateValue) => {
      const convertedValue = convertInputTextValue(
        stringConvertRule,
        updateValue
      ) as typeof updateValue;
      updateFieldValue(key, convertedValue);
      updateFieldTouched(key);
    },
    [updateFieldValue, updateFieldTouched, stringConvertRule]
  );

  const assortedDrillDownProperties = useAssortedDrillDownProperties();
  const assortedDrillDownPropertiesGroup = useMemo(() => {
    if (
      !assortedDrillDownProperties.length ||
      !isZipCodeInputFormat(inputFormat)
    ) {
      return undefined;
    }
    const prefectureId = inputFormat.searchSetting?.prefecture;
    if (prefectureId == null) {
      return undefined;
    }
    return assortedDrillDownProperties.find((group) =>
      group.some((property) => property.propertyId === prefectureId)
    );
  }, [assortedDrillDownProperties, inputFormat]);

  const handleAutoCompleteAddressButtonClick = useCallback(
    async (zipCode = '') => {
      if (!isZipCodeInputFormat(inputFormat)) {
        return;
      }
      if (!inputFormat.searchSetting) {
        return;
      }
      if (!zipCode) {
        return;
      }
      if (fieldErrorMessages) {
        return;
      }
      const addressData = await searchForAddress({ zipCode });
      if (!addressData) {
        return;
      }
      const { searchSetting } = inputFormat;
      autoCompleteAddress({
        addressData,
        formikProps: fieldProps.form,
        searchSetting,
        formPage,
        assortedDrillDownPropertiesGroup,
      });
    },
    [
      inputFormat,
      fieldErrorMessages,
      searchForAddress,
      fieldProps.form,
      formPage,
      assortedDrillDownPropertiesGroup,
    ]
  );

  if (fieldProps.field.value == null) {
    return null;
  }

  if (
    inputFormat.inputType === TEXT_PROPERTY_INPUT_TYPE.zipCode ||
    inputFormat.inputType === TEXT_PROPERTY_INPUT_TYPE.zipCodeWithoutHyphen
  ) {
    return (
      <TextZipCodeInput
        property={property}
        design={design}
        disabled={disabled || searching}
        value={fieldProps.field.value}
        onChange={handleChange}
        onBlur={handleBlur}
        errorMessages={fieldErrorMessages}
        touched={fieldTouched}
        submitted={submitted}
        onAutoCompleteAddressButtonClick={handleAutoCompleteAddressButtonClick}
        reEnter={reEnter}
      />
    );
  }

  return (
    <TextInputExcludeZipCode
      property={property}
      design={design}
      disabled={disabled}
      value={fieldProps.field.value}
      onChange={handleChange}
      onBlur={handleBlur}
      errorMessages={fieldErrorMessages}
      touched={fieldTouched}
      submitted={submitted}
      reEnter={reEnter}
    />
  );
};

export const Text: React.FC<Props> = (props) => {
  return (
    <Field name={props.property.propertyId}>
      {(fieldProps: FieldProps<FormPageInputItemText>) => (
        <Container {...props} fieldProps={fieldProps} />
      )}
    </Field>
  );
};
